import React, { useState, useRef, useEffect, useCallback } from 'react';
import { flushSync } from 'react-dom';
import { Send, Calendar, Menu, ChevronDown, ExternalLink, ChevronLeft, ChevronRight } from 'lucide-react';
import MessageBubble from './MessageBubble';
import CalendarWidget from './CalendarWidget';
import { MicrophoneButton } from './MicrophoneButton';
import { VoiceChatButton } from './VoiceChatButton';
import { VoiceChatModal } from './VoiceChatModal';
import { ConversationHeaderBrand } from './ConversationHeaderBrand';
import { useWebSocketQuery } from '../hooks/useWebSocketQuery';
import { useStreamingQuery } from '../hooks/useStreamingQuery';
import { useVoiceInput } from '../hooks/useVoiceInput';
import { RealtimeMessage } from '../hooks/useRealtimeVoice';
import { preloadAvatarRuntime } from '../features/liveavatar';

// Configuration for different streaming modes
// Options:
// - 'local-http': HTTP streaming to localhost:5000/api/query
// - 'local-websocket': WebSocket to ws://localhost:8765
// - 'cloud-http': HTTP streaming to https://a2wwcab90c.execute-api.eu-central-1.amazonaws.com/prod/query
// - 'cloud-websocket': WebSocket to wss://23e8rrwx6i.execute-api.eu-central-1.amazonaws.com/prod
const STREAMING_MODE = 'local-websocket' as const;

// Derived configuration
const USE_LOCAL_ENDPOINT = STREAMING_MODE.includes('local');
const USE_WEBSOCKET = STREAMING_MODE.includes('websocket');
const LANDING_SALLY_PROMPT_ROTATION_MS = 7000;
const SYNAWORKS_LANDING_URL = 'https://synaworks.ai/';

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'assistant';
  timestamp: Date;
  showCalendar?: boolean;
  audioData?: string; // Base64-encoded audio for assistant messages
  debugReasoning?: boolean; // Whether to show AI reasoning (debug mode)
  reasoningSummary?: string; // AI reasoning summary (from GPT-5 models)
}

interface ChatInterfaceProps {
  isDarkMode: boolean;
  onToggleTheme: () => void;
  onToggleSidebar: () => void;
  isAuthenticated?: boolean;
  embedded?: boolean;
  startWithLiveAvatar?: boolean;
  workspaceSlug?: string;
  agentSlug?: string;
  avatarProfileSlug?: string;
  companyName?: string;
  agentName?: string;
  titleFontFamily?: string;
  brandLogoUrl?: string;
  brandIconUrl?: string;
  brandBackgroundStyle?: React.CSSProperties;
  dashboardUrl?: string;
  publicExitUrl?: string;
}

const ChatInterface: React.FC<ChatInterfaceProps> = ({
  isDarkMode,
  onToggleTheme,
  onToggleSidebar,
  isAuthenticated = false,
  embedded = false,
  startWithLiveAvatar = false,
  workspaceSlug,
  agentSlug,
  avatarProfileSlug,
  companyName = 'Synaworks',
  agentName,
  titleFontFamily,
  brandLogoUrl,
  brandIconUrl,
  brandBackgroundStyle,
  dashboardUrl,
  publicExitUrl,
}) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [hasUsedConsultationStarter, setHasUsedConsultationStarter] = useState(false);
  const [inputText, setInputText] = useState('');
  const [showCalendar, setShowCalendar] = useState(false);
  const [savedCalendarData, setSavedCalendarData] = useState<any>(null);
  const [calendarConfigured, setCalendarConfigured] = useState<boolean | null>(null);
  const [isCalendarSidebarOpen, setIsCalendarSidebarOpen] = useState(false);
  const [currentStreamingMessageId, setCurrentStreamingMessageId] = useState<string | null>(null);
  const [hasReceivedContent, setHasReceivedContent] = useState(false);
  const [chatId, setChatId] = useState<string | null>(null);
  const [isResettingConversation, setIsResettingConversation] = useState(false);
  const [isVoiceChatOpen, setIsVoiceChatOpen] = useState(startWithLiveAvatar);
  const [voiceCloseRedirectUrl, setVoiceCloseRedirectUrl] = useState('');
  const [landingSallyPromptIndex, setLandingSallyPromptIndex] = useState(0);
  const [isChatModeForced, setIsChatModeForced] = useState(() => Boolean(workspaceSlug));
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const inputTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const calendarPrefetchTriggeredRef = useRef(false);
  const autoVoiceOpenedRef = useRef(false);
  const shouldScrollVoiceSummaryTopRef = useRef(false);
  const [isUserScrolling, setIsUserScrolling] = useState(false);
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true);
  const [isMobileLandingViewport, setIsMobileLandingViewport] = useState(
    () => window.matchMedia('(max-width: 639px)').matches
  );
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Conditional hook usage based on config
  const { response: wsResponse, sendQuery, isStreaming: wsIsStreaming, connectionState, registerMessageUpdateCallback: wsRegisterCallback, connect, disconnect, clearResponse } = useWebSocketQuery(USE_LOCAL_ENDPOINT, USE_WEBSOCKET);
  const { response: streamResponse, sendStreamingQuery, isStreaming: streamIsStreaming, registerMessageUpdateCallback: streamRegisterCallback, clearResponse: clearStreamResponse } = useStreamingQuery();

  // Voice input hook
  const { isRecording, isTranscribing, waveformData, startRecording, stopRecording, cancelRecording } = useVoiceInput({
    customerSlug: workspaceSlug,
    agentSlug: agentSlug || avatarProfileSlug,
  });
  
  // Use the appropriate response and methods based on config
  const response = USE_WEBSOCKET ? wsResponse : streamResponse;
  const isStreaming = USE_WEBSOCKET ? wsIsStreaming : streamIsStreaming;
  const isLocalhost = window.location.hostname === 'localhost';
  const API_BASE = isLocalhost ? 'http://localhost:5000' : '';
  const activeCalendarData = response.calendarData || savedCalendarData;
  const hasCalendarData = Boolean(activeCalendarData);
  const effectiveBrandIcon = brandIconUrl || '/favicon-synaworks.png';
  const effectiveBrandLogo = brandLogoUrl || (isDarkMode ? '/Logo_Synaworks_Conversation_Agent-white.png' : '/Logo_Synaworks_Conversation_Agent.png');
  const conversationAgentTitle = `${companyName} - Conversation Agent`;
  const isWorkspaceMode = Boolean(workspaceSlug);
  const showSynaworksBackLink = !embedded && !isWorkspaceMode;
  const effectiveAgentName = agentName?.trim() || 'Sally';
  const effectiveTitleFontFamily = titleFontFamily?.trim() || '"Insignia", Verdana, Geneva, sans-serif';
  const landingSallyPrompts = [
    `Hallo, freut mich Sie kennenzulernen. Was beschäftigt Sie gerade?`,
    `Wenn Sie möchten, ordne ich Ihr Vorhaben in zwei, drei Fragen mit Ihnen ein.`,
    `Viele IT-Transformationen geraten nicht an der Technik ins Stocken, sondern an Prioritäten, Rollen und Abstimmung.`,
    `Was läuft bei Ihnen schon gut und wo hakt es im Moment?`,
  ];
  const chatHeaderConnectionState = USE_WEBSOCKET
    ? (
        connectionState === 'connected'
          ? 'connected'
          : connectionState === 'error'
            ? 'error'
            : 'connecting'
      )
    : undefined;

  const handleOpenVoiceChat = useCallback(() => {
    setIsVoiceChatOpen(true);
  }, []);

  useEffect(() => {
    if (startWithLiveAvatar) {
      setIsVoiceChatOpen(true);
    }
  }, [startWithLiveAvatar]);

  useEffect(() => {
    if (workspaceSlug) {
      setIsChatModeForced(true);
    }
  }, [workspaceSlug]);

  useEffect(() => {
    if (autoVoiceOpenedRef.current) {
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const shouldOpenVoice = params.get('voice') === '1' || params.get('voice') === 'live';
    if (!shouldOpenVoice) {
      return;
    }

    autoVoiceOpenedRef.current = true;
    const isSallyPublicVoiceEntry =
      window.location.hostname === 'sally.synaworks.ai' &&
      params.get('mode') === 'prod';
    const isPublicDemoVoiceEntry = params.get('public') === 'demo' || Boolean(publicExitUrl);
    const closeRedirectUrl = publicExitUrl
      || (isSallyPublicVoiceEntry ? SYNAWORKS_LANDING_URL : '')
      || (isPublicDemoVoiceEntry ? SYNAWORKS_LANDING_URL : '');
    if (closeRedirectUrl) {
      setVoiceCloseRedirectUrl(closeRedirectUrl);
    }
    handleOpenVoiceChat();

    if (isSallyPublicVoiceEntry || isPublicDemoVoiceEntry) {
      return;
    }

    params.delete('voice');
    const nextSearch = params.toString();
    const nextUrl = `${window.location.pathname}${nextSearch ? `?${nextSearch}` : ''}${window.location.hash}`;
    window.history.replaceState(null, '', nextUrl);
  }, [handleOpenVoiceChat, publicExitUrl]);

  useEffect(() => {
    const mediaQuery = window.matchMedia('(max-width: 639px)');
    const updateMobileViewport = () => setIsMobileLandingViewport(mediaQuery.matches);

    updateMobileViewport();
    mediaQuery.addEventListener('change', updateMobileViewport);
    return () => mediaQuery.removeEventListener('change', updateMobileViewport);
  }, []);

  useEffect(() => {
    if (messages.length > 0) {
      return;
    }

    const intervalId = window.setInterval(() => {
      setLandingSallyPromptIndex((prev) => (prev + 1) % landingSallyPrompts.length);
    }, LANDING_SALLY_PROMPT_ROTATION_MS);

    return () => window.clearInterval(intervalId);
  }, [landingSallyPrompts.length, messages.length]);

  const resizeInputTextarea = useCallback((textarea: HTMLTextAreaElement | null) => {
    if (!textarea) {
      return;
    }

    if (!isMobileLandingViewport) {
      textarea.style.height = '';
      textarea.style.overflowY = '';
      return;
    }

    const maxHeight = messages.length === 0 ? 132 : 128;
    textarea.style.height = 'auto';
    textarea.style.height = `${Math.min(textarea.scrollHeight, maxHeight)}px`;
    textarea.style.overflowY = textarea.scrollHeight > maxHeight ? 'auto' : 'hidden';
  }, [isMobileLandingViewport, messages.length]);

  const handleInputTextChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInputText(e.target.value);
    resizeInputTextarea(e.currentTarget);
  }, [resizeInputTextarea]);

  useEffect(() => {
    resizeInputTextarea(inputTextareaRef.current);
  }, [inputText, resizeInputTextarea]);


  const prefetchCalendarData = useCallback(async () => {
    if (activeCalendarData) {
      return;
    }
    try {
      const customer = workspaceSlug || 'synaworks';
      const agent = agentSlug || 'sally';
      const adminToken = sessionStorage.getItem('admin_token');
      const params = new URLSearchParams({
        customer,
        agent,
        limit_slots: '10',
      });
      const res = await fetch(`${API_BASE}/api/calendar/booking-options?${params.toString()}`, {
        headers: adminToken ? { Authorization: `Bearer ${adminToken}` } : undefined,
      });
      if (!res.ok) {
        throw new Error(`Prefetch failed with status ${res.status}`);
      }
      const data = await res.json();
      if (data && typeof data === 'object') {
        if (data.configured === false) {
          setCalendarConfigured(false);
          setShowCalendar(false);
          setIsCalendarSidebarOpen(false);
        } else {
          setCalendarConfigured(true);
          setSavedCalendarData(data);
        }
      }
    } catch (err) {
      console.warn('Calendar prefetch failed:', err);
    }
  }, [API_BASE, activeCalendarData, agentSlug, workspaceSlug]);

  useEffect(() => {
    if (calendarPrefetchTriggeredRef.current) {
      return;
    }
    calendarPrefetchTriggeredRef.current = true;

    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let idleId: number | null = null;
    const run = () => {
      void prefetchCalendarData();
    };

    const requestIdle = (window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
    }).requestIdleCallback;

    if (requestIdle) {
      idleId = requestIdle(run, { timeout: 1500 });
    } else {
      timeoutId = setTimeout(run, 120);
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
      if (idleId !== null && 'cancelIdleCallback' in window) {
        (window as Window & { cancelIdleCallback?: (id: number) => void }).cancelIdleCallback?.(idleId);
      }
    };
  }, [prefetchCalendarData]);

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | null = null;
    let idleId: number | null = null;
    const requestIdle = (window as Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
    }).requestIdleCallback;
    if (requestIdle) {
      idleId = requestIdle(preloadAvatarRuntime, { timeout: 2500 });
    } else {
      timeoutId = setTimeout(preloadAvatarRuntime, 500);
    }
    return () => {
      if (timeoutId) clearTimeout(timeoutId);
      if (idleId !== null && 'cancelIdleCallback' in window) {
        (window as Window & { cancelIdleCallback?: (id: number) => void }).cancelIdleCallback?.(idleId);
      }
    };
  }, []);

  useEffect(() => {
    if (!isCalendarSidebarOpen || hasCalendarData) {
      return;
    }
    void prefetchCalendarData();
  }, [isCalendarSidebarOpen, hasCalendarData, prefetchCalendarData]);

  const scrollToBottom = useCallback((instant?: boolean) => {
    if (!shouldAutoScroll) return;
    const el = messagesEndRef.current;
    if (!el) return;

    // During streaming use instant scroll to avoid animation fighting DOM updates
    const behavior: ScrollBehavior = (instant || isStreaming) ? 'auto' : 'smooth';
    try {
      el.scrollIntoView({ behavior });
    } catch (err) {
      // Fallback for browsers that don't support scroll options
      try {
        el.scrollIntoView();
      } catch (_) {
        // Ignore if scrollIntoView is not supported
      }
    }
  }, [shouldAutoScroll, isStreaming]);

  // Check if user is near bottom of scroll area
  const isNearBottom = () => {
    const container = messagesContainerRef.current;
    if (!container) return true;
    
    const { scrollTop, scrollHeight, clientHeight } = container;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    return distanceFromBottom < 20; // Using your 20px threshold
  };

  // Handle scroll events to detect user scrolling (simplified for mobile compatibility)
  const handleScroll = () => {
    const nearBottom = isNearBottom();
    
    // During streaming, be very conservative - only disable if user scrolls far away
    if (isStreaming) {
      if (!nearBottom) {
        const container = messagesContainerRef.current;
        if (container) {
          const { scrollTop, scrollHeight, clientHeight } = container;
          const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
          // Only disable auto-scroll if user scrolls way up during streaming
          if (distanceFromBottom > 150) {
            setShouldAutoScroll(false);
          }
        }
      } else {
        setShouldAutoScroll(true);
      }
      return;
    }
    
    // Normal scroll handling when not streaming
    if (!nearBottom) {
      setIsUserScrolling(true);
      setShouldAutoScroll(false);
      
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      
      scrollTimeoutRef.current = setTimeout(() => {
        setIsUserScrolling(false);
        if (isNearBottom()) {
          setShouldAutoScroll(true);
        }
      }, 1000);
    } else {
      setIsUserScrolling(false);
      setShouldAutoScroll(true);
      
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    }
  };

  // Handle wheel events to immediately detect scroll intent (desktop)
  const handleWheel = useCallback((e: WheelEvent) => {
    // If user is actively trying to scroll, respect their intent immediately
    if (Math.abs(e.deltaY) > 0) {
      const scrollingUp = e.deltaY < 0;
      const scrollingDown = e.deltaY > 0;
      
      if (scrollingUp) {
        // User is scrolling up - disable auto-scroll immediately
        setIsUserScrolling(true);
        setShouldAutoScroll(false);
      } else if (scrollingDown) {
        // User is scrolling down - check if near bottom and re-enable if so
        const container = messagesContainerRef.current;
        if (container) {
          const { scrollTop, scrollHeight, clientHeight } = container;
          const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
          if (distanceFromBottom < 20) { // Using your 20px threshold
            setIsUserScrolling(false);
            setShouldAutoScroll(true);
          }
        }
      }
    }
  }, []);

  // Handle touch events for mobile scroll detection (simplified)
  const handleTouchStart = useCallback((e: TouchEvent) => {
    const touch = e.touches[0];
    if (touch) {
      (messagesContainerRef.current as any)._touchStartY = touch.clientY;
    }
  }, []);

  const handleTouchMove = useCallback((e: TouchEvent) => {
    const touch = e.touches[0];
    const container = messagesContainerRef.current;
    if (touch && container && (container as any)._touchStartY) {
      const deltaY = (container as any)._touchStartY - touch.clientY;

      // Significant upward swipe — disable auto-scroll
      if (deltaY > 50) {
        setIsUserScrolling(true);
        setShouldAutoScroll(false);
      }
    }
  }, []);

  // Auto-scroll only when appropriate
  useEffect(() => {
    if (messages.length === 0) return;

    if (shouldScrollVoiceSummaryTopRef.current) {
      return;
    }

    const lastMessage = messages[messages.length - 1];
    const isNewUserMessage = lastMessage.sender === 'user';

    // When the user sends a message, always scroll to bottom and re-enable auto-scroll
    if (isNewUserMessage) {
      setShouldAutoScroll(true);
      setIsUserScrolling(false);
      const el = messagesEndRef.current;
      if (el) {
        try {
          el.scrollIntoView({ behavior: 'smooth' });
        } catch (err) {
          try {
            el.scrollIntoView();
          } catch (_) {
            // Ignore if scrollIntoView is not supported
          }
        }
      }
      return;
    }

    // For assistant messages (streaming or complete), only scroll if user hasn't opted out
    if (shouldAutoScroll && !isUserScrolling) {
      scrollToBottom();
    }
  }, [messages, isStreaming, shouldAutoScroll, isUserScrolling, scrollToBottom]);

  useEffect(() => {
    if (!shouldScrollVoiceSummaryTopRef.current || messages.length === 0) {
      return;
    }

    shouldScrollVoiceSummaryTopRef.current = false;
    const container = messagesContainerRef.current;
    if (!container) {
      return;
    }

    requestAnimationFrame(() => {
      container.scrollTo({ top: 0, behavior: 'auto' });
    });
  }, [messages.length]);

  // Cleanup timeout on unmount and setup wheel/touch event listeners
  useEffect(() => {
    const container = messagesContainerRef.current;
    
    if (container) {
      // Desktop scroll detection
      container.addEventListener('wheel', handleWheel, { passive: true });
      // Mobile scroll detection
      container.addEventListener('touchstart', handleTouchStart, { passive: true });
      container.addEventListener('touchmove', handleTouchMove, { passive: true });
    }
    
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      if (container) {
        container.removeEventListener('wheel', handleWheel);
        container.removeEventListener('touchstart', handleTouchStart);
        container.removeEventListener('touchmove', handleTouchMove);
      }
    };
  }, [handleWheel, handleTouchStart, handleTouchMove]);

  // Update chat ID when we receive it from the API response
  useEffect(() => {
    // Use reset protection for both modes when resetting
    if (isResettingConversation) {
      console.log(`🚫 CHAT ID EFFECT - Ignoring ${USE_WEBSOCKET ? 'WebSocket' : 'HTTP streaming'} update because conversation is being reset`);
      return;
    }
    
    if (response.chatId && response.chatId !== chatId) {
      console.log(`🆔 CHAT ID UPDATE - Changing from '${chatId}' to '${response.chatId}' | Messages count: ${messages.length}`);
      console.log(`🆔 CHAT ID UPDATE - Current messages:`, messages.map(m => ({ sender: m.sender, text: m.text.substring(0, 50) + '...' })));
      setChatId(response.chatId);
    }
  }, [response.chatId, chatId, isResettingConversation, messages]);

  // Response timeout: if no content arrives within 30s, show error
  useEffect(() => {
    if (!currentStreamingMessageId || hasReceivedContent) return;
    const timer = setTimeout(() => {
      if (!hasReceivedContent && currentStreamingMessageId) {
        console.warn('⏰ Response timeout: no content received within 30s');
        setMessages(prev => prev.map(msg =>
          msg.id === currentStreamingMessageId && !msg.text
            ? { ...msg, text: 'Entschuldigung, es gab ein Problem bei der Verarbeitung. Bitte versuchen Sie es erneut.' }
            : msg
        ));
        setCurrentStreamingMessageId(null);
        setHasReceivedContent(false);
      }
    }, 30000);
    return () => clearTimeout(timer);
  }, [currentStreamingMessageId, hasReceivedContent]);

  // Update messages from WebSocket response when using WebSocket mode
  useEffect(() => {
    if (USE_WEBSOCKET && wsResponse.message && currentStreamingMessageId) {
      setMessages(prev => prev.map(msg =>
        msg.id === currentStreamingMessageId
          ? {
              ...msg,
              text: wsResponse.message,
              showCalendar: wsResponse.showCalendar,
              debugReasoning: wsResponse.debugReasoning,
              reasoningSummary: wsResponse.reasoningSummary
            }
          : msg
      ));
      
      // Mark that we've received content (hide loading dots)
      if (wsResponse.message.length > 0) {
        setHasReceivedContent(true);
      }
      
      // Update calendar state — only open, never auto-close (user dismisses manually)
      if (wsResponse.showCalendar) {
        setCalendarConfigured(true);
        setShowCalendar(true);
        setIsCalendarSidebarOpen(true);
        if (wsResponse.calendarData) {
          setSavedCalendarData(wsResponse.calendarData);
        }
      }

      // Reset currentStreamingMessageId when streaming is complete
      if (!wsIsStreaming && wsResponse.message.length > 0) {
        setCurrentStreamingMessageId(null);
      }
    }
  }, [wsResponse.message, wsResponse.showCalendar, wsResponse.calendarData, wsResponse.debugReasoning, wsResponse.reasoningSummary, currentStreamingMessageId, wsIsStreaming]);

  // Register callback for real-time message updates
  useEffect(() => {
    const updateMessage = (message: string, showCalendar: boolean) => {
      if (currentStreamingMessageId) {
        if (message.length > 0) {
          setHasReceivedContent(true);
        }

        setMessages(prev => prev.map(msg =>
          msg.id === currentStreamingMessageId
            ? {
                ...msg,
                text: message,
                showCalendar: showCalendar,
                debugReasoning: response.debugReasoning,
                reasoningSummary: response.reasoningSummary
              }
            : msg
        ));

        if (showCalendar) {
          setCalendarConfigured(true);
          setShowCalendar(true);
          setIsCalendarSidebarOpen(true);
        }
      }
    };

    // Register callback with the appropriate hook based on config
    if (USE_WEBSOCKET) {
      wsRegisterCallback(updateMessage);
    } else {
      streamRegisterCallback(updateMessage);
    }
  }, [currentStreamingMessageId, wsRegisterCallback, streamRegisterCallback, response.debugReasoning, response.reasoningSummary]);

  const handleSendMessage = async (messageText?: string) => {
    const textToSend = messageText || inputText;
    
    if (!textToSend.trim() || isStreaming) {
      return;
    }

    // Prepare conversation history BEFORE modifying messages state
    let conversationHistory: Array<{role: string, content: string}> = [];
    if (!chatId || hasUsedConsultationStarter) {
      // Capture current messages as conversation history (before adding new user message)
      // Filter out empty messages that might still be streaming
      conversationHistory = messages
        .filter(msg => msg.text.trim().length > 0)
        .map(msg => ({
          role: msg.sender === 'user' ? 'user' : 'assistant',
          content: msg.text
        }));
      
      console.log(`📋 DEBUG: Current messages array has ${messages.length} messages:`, messages);
      console.log(`📋 DEBUG: Conversation history has ${conversationHistory.length} messages:`, conversationHistory);
      console.log(`📋 DEBUG: hasUsedConsultationStarter: ${hasUsedConsultationStarter}`);
      console.log(`📋 DEBUG: Conversation history content:`, conversationHistory.map(h => `${h.role}: ${h.content.substring(0, 50)}...`));
      
      // Reset the flag after first use
      if (hasUsedConsultationStarter) {
        setHasUsedConsultationStarter(false);
      }
    } else {
      console.log(`📋 DEBUG: Using existing chatId: ${chatId}, not sending conversation history`);
    }

    const userMessage: Message = {
      id: Date.now().toString(),
      text: textToSend,
      sender: 'user',
      timestamp: new Date(),
    };

    console.log(`📨 Adding user message:`, userMessage);
    setMessages(prev => [...prev, userMessage]);
    setInputText('');

    // Create placeholder assistant message that will be updated with streaming content
    const assistantMessageId = (Date.now() + 1).toString();
    const assistantMessage: Message = {
      id: assistantMessageId,
      text: '',
      sender: 'assistant',
      timestamp: new Date(),
    };

    console.log(`🤖 Adding assistant placeholder message:`, assistantMessage);
    setMessages(prev => [...prev, assistantMessage]);
    setCurrentStreamingMessageId(assistantMessageId);
    setHasReceivedContent(false); // Reset content flag for new message
    console.log(`🎯 Set currentStreamingMessageId to: ${assistantMessageId}`);

    try {
      if (USE_WEBSOCKET) {
        const mode = USE_LOCAL_ENDPOINT ? 'Local WebSocket' : 'Cloud WebSocket';
        console.log(`📤 SEND MESSAGE - ${mode} query with chatId: ${chatId || 'null (new conversation)'}`);
        console.log(`📤 SEND MESSAGE - This ${chatId ? 'CONTINUES' : 'STARTS NEW'} conversation`);
        // Send query via WebSocket
        await sendQuery(
          textToSend,
          chatId || undefined,
          conversationHistory.length > 0 ? conversationHistory : undefined,
          workspaceSlug,
          agentSlug,
          avatarProfileSlug,
        );
      } else {
        // Use relative path for ngrok/proxy, absolute for direct localhost access
        const apiUrl = USE_LOCAL_ENDPOINT
          ? (isLocalhost ? 'http://localhost:5000/api/query' : '/api/query')
          : 'https://a2wwcab90c.execute-api.eu-central-1.amazonaws.com/prod/query';
        const mode = USE_LOCAL_ENDPOINT ? 'Local HTTP streaming' : 'Cloud HTTP streaming';
        console.log(`📤 SEND MESSAGE - ${mode} query with chatId: ${chatId || 'null (new conversation)'}`);
        console.log(`📤 SEND MESSAGE - This ${chatId ? 'CONTINUES' : 'STARTS NEW'} conversation`);
        // Start streaming query with chatId and conversation history
        await sendStreamingQuery(
          apiUrl,
          textToSend,
          chatId || undefined,
          conversationHistory.length > 0 ? conversationHistory : undefined,
          workspaceSlug,
          agentSlug,
          avatarProfileSlug,
        );
      }
      
    } catch (error) {
      console.error('Error calling backend API:', error);
      // Update the assistant message with error
      setMessages(prev => prev.map(msg => 
        msg.id === assistantMessageId 
          ? { 
              ...msg, 
              text: "I'm having trouble connecting to the backend. Please check your connection and try again. This might take a moment as the API is starting up." 
            }
          : msg
      ));
    } finally {
      // Only reset currentStreamingMessageId for HTTP streaming modes
      // For WebSocket modes, it will be reset when streaming completes
      if (!USE_WEBSOCKET) {
        setCurrentStreamingMessageId(null);
      }
    }
  };

  const handleOpenConversation = () => {
    if (chatId) {
      const params = new URLSearchParams();
      if (workspaceSlug) params.set('customer', workspaceSlug);
      if (agentSlug) params.set('agent', agentSlug);
      if (avatarProfileSlug) params.set('avatar', avatarProfileSlug);
      const queryString = params.toString();
      window.location.href = `/c/${chatId}${queryString ? `?${queryString}` : ''}`;
    }
  };

  // Handle voice chat close - add messages to conversation
  const handleVoiceChatClose = useCallback((voiceMessages?: RealtimeMessage[], voiceChatId?: string) => {
    setIsVoiceChatOpen(false);
    if (!voiceCloseRedirectUrl) {
      setIsChatModeForced(true);
    }

    // Persist voice session's chat_id so subsequent text messages continue the same conversation
    if (voiceChatId) {
      console.log('[VoiceChat] Setting chatId from voice session:', voiceChatId);
      setChatId(voiceChatId);
    }

    if (voiceMessages && voiceMessages.length > 0) {
      console.log('[VoiceChat] Adding messages from voice session:', voiceMessages.length);
      shouldScrollVoiceSummaryTopRef.current = true;
      setIsUserScrolling(true);
      setShouldAutoScroll(false);

      // Convert voice messages to UI messages (skip link bubbles — those are voice-only)
      const newMessages: Message[] = voiceMessages
        .filter(msg => msg.role === 'user' || msg.role === 'assistant')
        .map((msg, index) => ({
          id: `voice-${Date.now()}-${index}`,
          text: msg.content,
          sender: msg.role as 'user' | 'assistant',
          timestamp: new Date(),
        }));

      // Add to existing messages
      setMessages(prev => [...prev, ...newMessages]);
    }

    if (voiceCloseRedirectUrl) {
      window.location.replace(voiceCloseRedirectUrl);
    }
  }, [voiceCloseRedirectUrl]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Handle microphone button click
  const handleMicrophoneClick = async () => {
    if (isRecording) {
      // Stop recording and get transcript
      const transcript = await stopRecording();
      if (transcript) {
        // Preserve existing input and append new transcript chunk
        setInputText((prev) => {
          const existing = prev || '';
          const nextPart = transcript.trim();
          if (!nextPart) {
            return existing;
          }
          if (!existing.trim()) {
            return nextPart;
          }
          const separator = /[\s\n]$/.test(existing) ? '' : ' ';
          return `${existing}${separator}${nextPart}`;
        });
      }
    } else {
      // Start recording
      await startRecording();
    }
  };

  // iPhone Firefox focus fix
  const handleInputFocus = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    // Critical fix for iPhone Firefox script error on textarea focus
    const isIPhoneFirefox = navigator.userAgent.includes('FxiOS') && navigator.userAgent.includes('iPhone');
    
    if (isIPhoneFirefox) {
      // Immediately prevent the problematic focus event
      e.preventDefault();
      e.stopPropagation();
      
      // Don't even try to focus - just return immediately
      console.log('🚨 iPhone Firefox focus blocked to prevent script error');
      return; // Critical: exit early to prevent other focus handling
    }
    
    // iOS Safari scroll handling (not Firefox)
    if (/iPad|iPhone|iPod/.test(navigator.userAgent) && !navigator.userAgent.includes('FxiOS')) {
      setTimeout(() => {
        try {
          if (e.target) {
            e.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
        } catch (error) {
          // Silently handle scroll errors
        }
      }, 300);
    }
  };

  const handleBackToHome = () => {
    console.log('🖼️ LOGO CLICKED - Starting new conversation');

    // Use reset protection for both modes to prevent chatId restoration
    const mode = USE_WEBSOCKET ? 'WEBSOCKET' : 'HTTP STREAMING';
    console.log(`🖼️ ${mode} - Starting new conversation with reset protection`);
    setIsResettingConversation(true);

    // Clear response state first to prevent chatId restoration
    if (USE_WEBSOCKET) {
      clearResponse();
    } else {
      clearStreamResponse();
    }

    flushSync(() => {
      setMessages([]);
      setShowCalendar(false);
      setIsCalendarSidebarOpen(false);
      setCurrentStreamingMessageId(null);
      setHasReceivedContent(false);
      setInputText('');
      setChatId(null);
      setIsChatModeForced(false);
    });

    // Clear reset flag after a short delay
    setTimeout(() => {
      console.log(`🖼️ ${mode} - Clearing reset flag`);
      setIsResettingConversation(false);
    }, 100);

    // Handle WebSocket reconnection if needed
    if (USE_WEBSOCKET) {
      disconnect();
      setTimeout(() => connect(), 150);
    }
  };
  const showWelcomeScreen = messages.length === 0 && !isChatModeForced;
  const showChatInput = messages.length > 0 || isChatModeForced;
  const shouldShowChatHeader = messages.length > 0 && !embedded;
  const handleHeaderHomeClick = () => {
    if (dashboardUrl) {
      window.location.assign(dashboardUrl);
      return;
    }
    handleBackToHome();
  };

  const voiceChatModal = (
    <VoiceChatModal
      isOpen={isVoiceChatOpen}
      onClose={handleVoiceChatClose}
      chatId={chatId || undefined}
      isDarkMode={isDarkMode}
      companyName={companyName}
      agentName={effectiveAgentName}
      brandIconUrl={effectiveBrandIcon}
      workspaceSlug={workspaceSlug}
      agentSlug={agentSlug}
      avatarProfileSlug={avatarProfileSlug}
      calendarEnabled={calendarConfigured === true}
      dashboardUrl={dashboardUrl}
      publicExitUrl={publicExitUrl || voiceCloseRedirectUrl || undefined}
    />
  );

  return (
    <div className={`chat-interface-root flex flex-col chat-container ${embedded ? 'h-full' : 'h-screen'}`} style={brandBackgroundStyle}>
      {/* Header - Only show when there are messages */}
      {shouldShowChatHeader && (
        <div className={`chat-mode-header flex items-center justify-between p-4 border-b print-hide ${
          isDarkMode 
            ? 'bg-gray-800 border-gray-700' 
            : 'bg-white border-gray-200'
        } ${embedded ? 'pl-20 sm:pl-24' : ''}`}>
          <div className="chat-mode-header-left flex min-w-0 items-center gap-3">
            {showSynaworksBackLink && (
              <a
                href={SYNAWORKS_LANDING_URL}
                className={`inline-flex shrink-0 items-center gap-1.5 rounded-full border px-3 py-2 text-sm font-medium shadow-sm transition-colors ${
                  isDarkMode
                    ? 'border-gray-700 bg-gray-900 text-gray-200 hover:bg-gray-800'
                    : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                }`}
                title="Zurück zu Synaworks.ai"
              >
                <ChevronLeft className="h-4 w-4" />
                <span className="hidden sm:inline">Zurück zu Synaworks.ai</span>
                <span className="sm:hidden">Zurück</span>
              </a>
            )}
            {!embedded && isAuthenticated ? (
              <button
                onClick={onToggleSidebar}
                className={`shrink-0 p-2.5 rounded-lg transition-colors ${
                  isDarkMode
                    ? 'hover:bg-gray-700 text-gray-300'
                    : 'hover:bg-gray-100 text-gray-600'
                }`}
              >
                <Menu size={20} />
              </button>
            ) : null}
            <ConversationHeaderBrand
              isDarkMode={isDarkMode}
              title={conversationAgentTitle}
              subtitle={`KI-gestützte virtuelle Ansprechperson ${effectiveAgentName}`}
              brandIconSrc={effectiveBrandIcon}
              brandIconAlt={companyName}
              onHomeClick={handleHeaderHomeClick}
              connectionState={chatHeaderConnectionState}
            />
          </div>
          
          <div className="chat-mode-actions flex items-center gap-2">
            {chatId && (
              <button
                onClick={handleOpenConversation}
                className={`p-2.5 rounded-lg transition-all duration-150 transform active:scale-95 active:bg-opacity-80 ${
                  isDarkMode
                    ? 'hover:bg-gray-700 text-gray-300 active:bg-gray-600'
                    : 'hover:bg-gray-100 text-gray-600 active:bg-gray-200'
                }`}
                title="Gespräch öffnen"
              >
                <ExternalLink size={18} />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Chat Area */}
      <div className="relative flex flex-1 overflow-hidden">
        {messages.length > 0 && calendarConfigured === true && (
          <button
            type="button"
            onClick={() => setIsCalendarSidebarOpen(prev => !prev)}
            className={`absolute right-3 top-1/2 z-30 hidden -translate-y-1/2 items-center gap-1 rounded-full border px-3 py-2 text-xs font-semibold shadow-lg transition-colors lg:flex ${
              isDarkMode
                ? 'border-gray-700 bg-gray-900/95 text-gray-200 hover:bg-gray-800'
                : 'border-gray-300 bg-white/95 text-gray-700 hover:bg-gray-100'
            }`}
            aria-label={isCalendarSidebarOpen ? 'Kalender einklappen' : 'Kalender ausklappen'}
            title={isCalendarSidebarOpen ? 'Kalender einklappen' : 'Kalender ausklappen'}
          >
            <Calendar className="h-4 w-4" />
            {isCalendarSidebarOpen ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </button>
        )}

        <div className={`flex-1 flex flex-col min-w-0 relative ${calendarConfigured === true && isCalendarSidebarOpen ? 'lg:max-w-[calc(100%-20rem)]' : ''}`}>
          {/* Messages or Welcome Screen */}
          <div 
            className={`chat-scroll-region flex-1 ${showWelcomeScreen ? 'overflow-hidden' : 'overflow-y-auto'}`}
            ref={messagesContainerRef}
            onScroll={handleScroll}
            style={brandBackgroundStyle}
          >
            {showWelcomeScreen ? (
              /* Voice-first welcome screen. The previous text-first landing remains in git history. */
                <div className={`landing-welcome-screen landing-welcome-screen-v2 flex h-full min-h-0 w-full flex-col items-center overflow-hidden px-4 sm:px-6 lg:px-8 relative ${embedded ? 'pt-12 sm:pt-16' : ''}`}>
                {(showSynaworksBackLink || (!embedded && isAuthenticated)) && (
                  <div className="fixed left-4 top-4 z-10 flex items-center gap-2">
                    {showSynaworksBackLink && (
                      <a
                        href={SYNAWORKS_LANDING_URL}
                        className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-2 text-sm font-medium shadow-sm backdrop-blur transition-colors ${
                          isDarkMode
                            ? 'border-white/10 bg-[#1a1a1a]/85 text-gray-100 hover:bg-black'
                            : 'border-[#e6e6e7] bg-white/90 text-[#57555c] hover:bg-[#f1f2f2]'
                        }`}
                        title="Zurück zu Synaworks.ai"
                      >
                        <ChevronLeft className="h-4 w-4" />
                        <span className="hidden sm:inline">Zurück zu Synaworks.ai</span>
                        <span className="sm:hidden">Zurück</span>
                      </a>
                    )}
                    {!embedded && isAuthenticated ? (
                      <button
                        onClick={onToggleSidebar}
                        className={`p-2.5 rounded-lg transition-colors ${
                          isDarkMode
                            ? 'hover:bg-gray-700 text-gray-300'
                            : 'hover:bg-gray-100 text-gray-600'
                        }`}
                        aria-label="Menü öffnen"
                        title="Menü öffnen"
                      >
                        <Menu size={20} />
                      </button>
                    ) : null}
                  </div>
                )}

                <div className="landing-v2-ambient landing-v2-ambient-a" />
                <div className="landing-v2-ambient landing-v2-ambient-b" />

                <div className="landing-v2-content relative z-[1] flex min-h-0 w-full max-w-6xl flex-1 flex-col items-center justify-center py-6 sm:py-8 lg:py-10">
                  {/* Logo */}
                  <div className={`landing-logo landing-v2-logo mb-5 sm:mb-7 ${isWorkspaceMode ? 'flex w-full max-w-4xl items-center justify-center' : 'flex justify-center'}`}>
                    {isWorkspaceMode ? (
                      <div className="flex max-w-full items-center gap-4 sm:gap-5">
                        <img 
                          src={effectiveBrandLogo} 
                          alt={conversationAgentTitle}
                          className="h-auto w-auto max-w-[96px] object-contain sm:max-w-[120px] lg:max-w-[150px]"
                          style={{ width: 'auto' }}
                        />
                        <div className="min-w-0 max-w-[min(68vw,42rem)] text-left">
                          <h1
                            className={`break-words text-3xl font-semibold leading-[0.95] tracking-tight sm:text-4xl lg:text-5xl ${
                              isDarkMode ? 'text-white' : 'text-gray-900'
                            }`}
                            style={{ fontFamily: effectiveTitleFontFamily }}
                          >
                            {companyName}
                          </h1>
                          <p
                            className={`mt-2 text-lg font-medium tracking-[0.08em] sm:text-2xl lg:text-3xl ${
                              isDarkMode ? 'text-gray-400' : 'text-gray-500'
                            }`}
                          >
                            Conversation Agent
                          </p>
                        </div>
                      </div>
                    ) : (
                      <img 
                        src={effectiveBrandLogo} 
                        alt="Synaworks"
                        className="h-auto max-w-full object-contain max-h-[72px] sm:max-h-[90px] lg:max-h-[104px]"
                        style={{ width: 'auto' }}
                      />
                    )}
                  </div>

                  <div className={`landing-v2-card w-full max-w-4xl rounded-[2rem] border p-5 shadow-2xl backdrop-blur-xl sm:p-8 lg:p-10 ${
                    isDarkMode
                      ? 'border-white/10 bg-[#1a1a1a]/88 text-white shadow-black/30'
                      : 'border-[#e6e6e7] bg-white/92 text-[#1a1a1a] shadow-slate-900/10'
                  }`}>
                    <div className="grid items-center gap-8 lg:grid-cols-[1fr_auto] lg:gap-10">
                      <div className="text-left">
                        <div className={`landing-v2-eyebrow mb-4 inline-flex rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-[0.24em] ${
                          isDarkMode
                            ? 'border-[#ed7203]/35 bg-[#ed7203]/15 text-orange-100'
                            : 'border-[#e6e6e7] bg-[#f1f2f2] text-[#57555c]'
                        }`}>
                          Live Voice Assistant
                        </div>
                        <h1
                          className={`landing-v2-title text-4xl font-semibold leading-[0.96] tracking-[-0.045em] sm:text-5xl lg:text-6xl ${
                            isDarkMode ? 'text-white' : 'text-[#1a1a1a]'
                          }`}
                          style={{ fontFamily: effectiveTitleFontFamily }}
                        >
                          Lernen Sie {effectiveAgentName} im Gespräch kennen.
                        </h1>
                        <div className={`landing-v2-intro mt-6 rounded-3xl border p-5 sm:p-6 ${
                          isDarkMode
                            ? 'border-white/10 bg-white/[0.06] text-slate-100'
                            : 'border-[#e6e6e7] bg-[#f7f7f7] text-[#57555c]'
                          }`}>
                          <p className="text-lg font-medium leading-relaxed sm:text-xl">
                            Hallo, ich bin {effectiveAgentName}, Ihre virtuelle Assistentin bei {companyName}. Erzählen Sie mir kurz, worum es bei Ihnen gerade geht.
                          </p>
                          <div className={`landing-v2-insight mt-5 rounded-2xl border px-4 py-3 text-sm leading-6 sm:text-base ${
                            isDarkMode
                              ? 'border-[#ed7203]/20 bg-[#ed7203]/10 text-slate-300'
                              : 'border-[#f4d2b3] bg-[#fff3e8] text-[#57555c]'
                          }`}>
                            <span className={`mb-1 block text-xs font-semibold uppercase tracking-[0.18em] ${
                              isDarkMode ? 'text-orange-100' : 'text-[#c75f02]'
                            }`}>
                              Worum es im Gespräch geht
                            </span>
                            Oft wird erst im Gespräch klar, ob es um Strategie, Organisation, Umsetzung oder Akzeptanz geht. Genau dort setzt {effectiveAgentName} an.
                          </div>
                        </div>
                      </div>

                      <div className="landing-v2-action-panel flex flex-col items-center">
                        <div className={`landing-v2-avatar-shell relative flex h-56 w-56 items-center justify-center rounded-full sm:h-64 sm:w-64 ${
                          isDarkMode ? 'bg-[#ed7203]/10' : 'bg-[#fff3e8]'
                        }`}>
                          <div className="landing-v2-orbit landing-v2-orbit-one" />
                          <div className="landing-v2-orbit landing-v2-orbit-two" />
                          <div className={`landing-v2-avatar-core flex h-36 w-36 items-center justify-center rounded-full text-5xl font-semibold shadow-2xl sm:h-40 sm:w-40 ${
                            isDarkMode
                              ? 'bg-gradient-to-br from-[#ed7203] to-[#e0a240] text-white shadow-black/40'
                              : 'bg-gradient-to-br from-[#1a1a1a] to-[#ed7203] text-white shadow-orange-900/20'
                          }`}>
                            {effectiveAgentName.charAt(0).toUpperCase()}
                          </div>
                        </div>

                        <div className="landing-v2-voice-button-slot mt-[-2.25rem]">
                          <VoiceChatButton
                            onClick={handleOpenVoiceChat}
                            onIntent={preloadAvatarRuntime}
                            disabled={isStreaming}
                            isDarkMode={isDarkMode}
                          />
                        </div>
                        <p className={`mt-5 text-center text-sm font-semibold uppercase tracking-[0.18em] ${
                          isDarkMode ? 'text-orange-100' : 'text-[#57555c]'
                        }`}>
                          Gespräch starten
                        </p>
                        <div className={`landing-v2-sally-bubble mt-4 max-w-xs rounded-[1.35rem] px-4 py-3 text-left text-sm leading-6 shadow-lg ${
                          isDarkMode
                            ? 'bg-white/10 text-slate-100 shadow-black/20'
                            : 'bg-white text-[#57555c] shadow-slate-900/10'
                        }`}>
                          <span className={`mb-1 block text-xs font-semibold uppercase tracking-[0.16em] ${
                            isDarkMode ? 'text-orange-100' : 'text-[#c75f02]'
                          }`}>
                            {effectiveAgentName} sagt
                          </span>
                          <span key={landingSallyPromptIndex} className="landing-v2-rotating-copy block">
                            {landingSallyPrompts[landingSallyPromptIndex]}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* AI disclaimer */}
                <p className={`text-center text-[11px] mt-6 mb-4 sm:mb-6 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  {effectiveAgentName} nutzt KI. Ergebnisse bitte überprüfen.
                </p>

              </div>
            ) : (
              /* Messages View */
              <div className="chat-message-stack p-2 sm:p-4">
                <div className="max-w-4xl mx-auto space-y-3 sm:space-y-4">
                  {messages.map((message) => {
                    const isCurrentStreaming = message.id === currentStreamingMessageId;
                    const shouldShowDots = isCurrentStreaming && isStreaming && !hasReceivedContent;
                    // console.log(`🔍 Rendering message ${message.id}: text="${message.text.substring(0, 30)}...", isCurrentStreaming=${isCurrentStreaming}, shouldShowDots=${shouldShowDots}`);
                    
                    return (
                      <MessageBubble
                        key={message.id}
                        message={message}
                        isDarkMode={isDarkMode}
                        showLoadingDots={shouldShowDots}
                        isStreaming={isCurrentStreaming && isStreaming}
                        customerSlug={workspaceSlug}
                        agentSlug={agentSlug || avatarProfileSlug}
                        onCalendarClick={() => {
                          setShowCalendar(true);
                          setIsCalendarSidebarOpen(true);
                        }}
                      />
                    );
                  })}
                  <div ref={messagesEndRef} />
                </div>
              </div>
            )}
          </div>



          {/* Scroll to Bottom Button - Show when user has scrolled away from messages */}
          {messages.length > 0 && !shouldAutoScroll && (
            <div className="absolute bottom-24 right-2 sm:right-4 z-10">
              <button
                onClick={() => {
                  setShouldAutoScroll(true);
                  setIsUserScrolling(false);
                  if (scrollTimeoutRef.current) {
                    clearTimeout(scrollTimeoutRef.current);
                  }
                  scrollToBottom();
                }}
                className={`p-3 rounded-full shadow-lg transition-all hover:scale-105 ${
                  isDarkMode 
                    ? 'bg-gray-700 hover:bg-gray-600 text-gray-300' 
                    : 'bg-white hover:bg-gray-50 text-gray-600'
                } border-2 ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}
                title="Scroll to bottom"
              >
                <ChevronDown size={20} />
              </button>
            </div>
          )}

          {/* Input Area - Only show when there are messages */}
          {showChatInput && (
            <div className={`chat-input-area border-t p-2 sm:p-4 pb-[max(0.5rem,env(safe-area-inset-bottom))] sm:pb-[max(1rem,env(safe-area-inset-bottom))] ${
              isDarkMode
                ? 'bg-gray-800 border-gray-700'
                : 'bg-white border-gray-200'
            }`}>
              <div className="max-w-4xl mx-auto">
                <div className="chat-input-row relative flex items-center gap-2">
                  {!isRecording && !isTranscribing && (
                    <textarea
                      ref={inputTextareaRef}
                      value={inputText}
                      onChange={handleInputTextChange}
                      onKeyDown={handleKeyDown}
                      onFocus={handleInputFocus}
                      placeholder={isMobileLandingViewport ? 'Fragen Sie mich einfach!' : 'Vor welchen Herausforderungen stehen Sie gerade?'}
                      rows={isMobileLandingViewport ? 1 : 2}
                      spellCheck={false}
                      className={`chat-input-textarea flex-1 min-w-0 resize-none rounded-xl px-3 sm:px-4 py-2 sm:py-3 focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm sm:text-base ${
                        isDarkMode
                          ? 'bg-gray-700 text-white placeholder-gray-400 border-gray-600'
                          : 'bg-gray-50 text-gray-900 placeholder-gray-500 border-gray-300'
                      } border`}
                    />
                  )}
                  <div className={`flex items-center gap-1 ${isRecording || isTranscribing ? 'flex-1' : 'flex-shrink-0'}`}>
                    <MicrophoneButton
                      isRecording={isRecording}
                      isTranscribing={isTranscribing}
                      waveformData={waveformData}
                      onClick={handleMicrophoneClick}
                      onCancel={cancelRecording}
                      disabled={isStreaming}
                      wide={isRecording || isTranscribing}
                    />
                  </div>
                  <button
                    onClick={() => handleSendMessage()}
                    disabled={!inputText.trim() || isStreaming}
                    className={`chat-send-button p-2.5 sm:p-3 rounded-lg transition-colors flex-shrink-0 ${
                      !inputText.trim() || isStreaming
                        ? isDarkMode
                          ? 'text-gray-400 cursor-not-allowed'
                          : 'text-gray-400 cursor-not-allowed opacity-50'
                        : isDarkMode
                          ? 'bg-[#ed7203] hover:bg-[#c75f02] text-white'
                          : 'bg-[#ed7203] hover:bg-[#c75f02] text-white'
                    }`}
                  >
                    <Send size={20} className="sm:hidden" />
                    <Send size={24} className="hidden sm:block" />
                  </button>
                </div>
                <p className={`mt-1.5 text-center text-[11px] ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                  {effectiveAgentName} nutzt KI. Ergebnisse bitte überprüfen.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Calendar Sidebar */}
        {calendarConfigured === true && (
          <div className={`relative hidden lg:block lg:min-h-0 lg:shrink-0 lg:transition-[width] lg:duration-300 lg:ease-out ${
            isCalendarSidebarOpen ? 'lg:w-80' : 'lg:w-0'
          }`}>
            <div className={`absolute inset-y-0 right-0 w-80 min-h-0 border-l transition-transform duration-300 ease-out ${
              isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
            } ${isCalendarSidebarOpen ? 'translate-x-0' : 'translate-x-full'}`}>
              {hasCalendarData ? (
                <CalendarWidget
                  isDarkMode={isDarkMode}
                  onClose={() => {
                    setShowCalendar(false);
                    setIsCalendarSidebarOpen(false);
                  }}
                  calendarData={activeCalendarData}
                />
              ) : (
                <div className={`flex h-full items-center justify-center px-6 text-center text-sm ${
                  isDarkMode ? 'text-gray-400' : 'text-gray-500'
                }`}>
                  Terminvorschläge werden im Hintergrund geladen.
                </div>
              )}
            </div>
          </div>
        )}

        {/* Mobile Calendar Overlay (only on mobile/tablet) */}
        {calendarConfigured === true && showCalendar && (
          <div className="lg:hidden">
            <CalendarWidget
              isDarkMode={isDarkMode}
              onClose={() => setShowCalendar(false)}
              calendarData={activeCalendarData}
            />
          </div>
        )}
      </div>

      {/* Voice Chat Modal */}
      {voiceChatModal}
    </div>
  );
};

export default ChatInterface;
